import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router';

import { QUERY_DEFAULT_VALUE, REMOVED_ALL_CATEGORIES_VALUE } from '../model';

interface HookReturn {
  query: string;
  selectedCategories: string[];
  onFilter: (filterType: 'query' | 'category', value: string | string[]) => void;
}

export const useServicesFilters = (defaultCategories: string[]): HookReturn => {
  const [searchParams, setSearchParams] = useSearchParams();

  const query = searchParams.get('q') ?? QUERY_DEFAULT_VALUE;
  const selectedCategories = searchParams.getAll('category');
  const shouldUseDefaultCategories = query === QUERY_DEFAULT_VALUE && !selectedCategories.length;

  const onFilter = useCallback(
    (filterType: 'query' | 'category', value: string | string[]) => {
      // TODO: if params grows, rewrite code to save previous params dynamically
      setSearchParams((prevParams) => {
        switch (filterType) {
          case 'query':
            return {
              category: prevParams.getAll('category'),
              q: value,
            };

          case 'category': {
            switch (value) {
              case 'all': {
                const isAllCategoriesSelected =
                  selectedCategories.length === defaultCategories.length || shouldUseDefaultCategories;

                return {
                  q: prevParams.get('q') ?? QUERY_DEFAULT_VALUE,
                  category:
                    isAllCategoriesSelected || !selectedCategories.length
                      ? REMOVED_ALL_CATEGORIES_VALUE
                      : defaultCategories,
                };
              }

              default:
                return {
                  q: prevParams.get('q') ?? QUERY_DEFAULT_VALUE,
                  category: value.length ? value : QUERY_DEFAULT_VALUE,
                };
            }
          }
        }
      });
    },
    [setSearchParams, defaultCategories, selectedCategories, shouldUseDefaultCategories],
  );

  useEffect(() => {
    if (
      (selectedCategories.length === defaultCategories.length || !selectedCategories.length) &&
      query === QUERY_DEFAULT_VALUE
    ) {
      setSearchParams({});
    }
  }, [setSearchParams, shouldUseDefaultCategories, query, defaultCategories.length, selectedCategories.length]);

  return {
    query,
    selectedCategories:
      !selectedCategories.length || selectedCategories?.length === defaultCategories.length
        ? defaultCategories
        : selectedCategories,
    onFilter,
  };
};
